import { Currency, UserType } from '@/connect/gen/common/protos/types_pb';
import {
  AgreementStatus,
  CollateralMethod,
  CollateralType,
  DeliveryType,
  SettlementSystem,
  TermType,
} from '@/connect/gen/consts/agreementconsts_pb';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { ExecutionStatus } from '@/connect/gen/consts/executionconsts_pb';
import { ContractStatus, RoundingRule } from '@/connect/gen/consts/loanconsts_pb';
import { SecurityCategory, SecurityType } from '@/connect/gen/consts/instrumentconsts_pb';
import { OmsOrderStatus, OmsTimeInForceType } from '@/connect/gen/consts/omsconsts_pb';
import { SettlementType } from '@/connect/gen/consts/settlementconsts_pb';
import { timeInForceAbbr } from '@/modules/marketplace/helpers/marketplace';

export * from '@/connect/gen/consts/agreementconsts_pb';
export * from '@/connect/gen/consts/benchmarkconsts_pb';
export * from '@/connect/gen/consts/commonconsts_pb';
export * from '@/connect/gen/consts/executionconsts_pb';
export * from '@/connect/gen/consts/loanbuyinconsts_pb';
export * from '@/connect/gen/consts/loanconsts_pb';
export * from '@/connect/gen/consts/loanhistoryconsts_pb';
export * from '@/connect/gen/consts/loanrecallcancelconsts_pb';
export * from '@/connect/gen/consts/loanrecallconsts_pb';
export * from '@/connect/gen/consts/loanrequestconsts_pb';
export * from '@/connect/gen/consts/loanreturnconsts_pb';
export * from '@/connect/gen/consts/omsconsts_pb';
export * from '@/connect/gen/consts/queryconsts_pb';
export * from '@/connect/gen/consts/ratemodificationconsts_pb';
export * from '@/connect/gen/consts/settlementconsts_pb';
export * from '@/connect/gen/consts/uptimeconsts_pb';

export const orderStatuses = new Map([
  [OmsOrderStatus.OPEN, 'Open'],
  [OmsOrderStatus.FILLED, 'Filled'],
  [OmsOrderStatus.CANCELED, 'Canceled'],
  [OmsOrderStatus.EXPIRED, 'Expired'],
  [OmsOrderStatus.TERMINATED, 'Terminated'],
  [OmsOrderStatus.HELD, 'Held'],
  [OmsOrderStatus.FORCE_CANCELED, 'Force Canceled'],
]);
export const availableOrderStatuses = [...orderStatuses.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const agreementStatuses = new Map([
  [AgreementStatus.DRAFT, { name: 'Draft', color: 'blue' }],
  [AgreementStatus.ACTIVE, { name: 'Active', color: 'green' }],
  [AgreementStatus.REJECTED, { name: 'Rejected', color: 'red' }],
  [AgreementStatus.CANCELED, { name: 'Closed', color: 'gray' }],
  [AgreementStatus.INACTIVE, { name: 'Inactive', color: 'gray' }],
]);

export const sides = new Map([
  [Side.BORROW, 'Borrower'],
  [Side.LOAN, 'Lender'],
]);
export const availableSides = [...sides.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const settlementTypes = new Map([
  [SettlementType.NSCC, 'NSCC'],
  [SettlementType.BILATERAL, 'Bilateral'],
  [SettlementType.OCC, 'OCC'],
]);

export const availableSettlementTypes = [...settlementTypes.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const roundingRules = new Map([
  [RoundingRule.NO_ROUNDING, 'no rounding'],
  [RoundingRule.UP_TO_NEAREST_0_DOT_01, 'up to nearest $0.01'],
  [RoundingRule.UP_TO_NEAREST_0_DOT_05, 'up to nearest $0.05'],
  [RoundingRule.UP_TO_NEAREST_0_DOT_10, 'up to nearest $0.10'],
  [RoundingRule.UP_TO_NEAREST_0_DOT_25, 'up to nearest $0.25'],
  [RoundingRule.UP_TO_NEAREST_1_DOT_0, 'up to nearest $1.00'],
]);
export const availableRoundingRules = [...roundingRules.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const collateralTypes = new Map([
  [CollateralType.CASH, 'Cash'],
  [CollateralType.NON_CASH_UST, 'Non-cash (UST)'],
  [CollateralType.NON_CASH_EQ, 'Non-Cash (Eq)'],
  [CollateralType.NON_CASH_JGB, 'Non-Cash (JGB)'],
]);
export const availableCollateralTypes = [...collateralTypes.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const currencies = new Map([
  [Currency.USD, 'USD'],
  [Currency.CAD, 'CAD'],
  [Currency.GBP, 'GBP'],
  [Currency.EUR, 'EUR'],
  [Currency.JPY, 'JPY'],
]);
export const availableCurrencies = [...currencies.entries()].map(([id, name]) => ({ id, name }));

export const collateralMethods = new Map([
  [CollateralMethod.PLEDGE, 'Pledge (PL)'],
  [CollateralMethod.TITLE_TRANSFER, 'Title Transfer (TT)'],
]);

export const availableCollateralMethods = [...collateralMethods.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const deliveryTypes = new Map([
  [DeliveryType.DVP, 'DVP'],
  [DeliveryType.FOP, 'FOP'],
]);

export const availableDeliveryTypes = [...deliveryTypes.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const termTypes = new Map([
  [TermType.NONE, 'None'],
  [TermType.FIXED, 'Fixed'],
  [TermType.EVERGREEN, 'Evergreen'],
  [TermType.ONE_DAY, '1day'],
  [TermType.BULLET, 'Bullet'],
]);
export const availableTermTypes = [...termTypes.entries()].map(([id, name]) => ({ id, name }));

export const settlementSystems = new Map([
  [SettlementSystem.PROVABLE_MARKET, 'Provable'],
  [SettlementSystem.LOANET, 'Loanet'],
  [SettlementSystem.LOANET_SFT, 'Loanet.SFT'],
  [SettlementSystem.SPIRE, 'Spire'],
  [SettlementSystem.PROP, 'Prop'],
]);
export const availableSettlementSystems = [...settlementSystems.entries()].map(([id, name]) => ({
  id,
  name,
}));

export const timeInForceTypes = new Map([
  [OmsTimeInForceType.DAY, timeInForceAbbr(OmsTimeInForceType[OmsTimeInForceType.DAY])],
  [
    OmsTimeInForceType.GOOD_TILL_CANCEL,
    timeInForceAbbr(OmsTimeInForceType[OmsTimeInForceType.GOOD_TILL_CANCEL]),
  ],
  [
    OmsTimeInForceType.IMMEDIATE_OR_CANCEL,
    timeInForceAbbr(OmsTimeInForceType[OmsTimeInForceType.IMMEDIATE_OR_CANCEL]),
  ],
  [
    OmsTimeInForceType.FILL_OR_KILL,
    timeInForceAbbr(OmsTimeInForceType[OmsTimeInForceType.FILL_OR_KILL]),
  ],
  [
    OmsTimeInForceType.GOOD_TILL_TIME,
    timeInForceAbbr(OmsTimeInForceType[OmsTimeInForceType.GOOD_TILL_TIME]),
  ],
]);

export const userTypes = new Map([
  [UserType.TRADER, 'trader'],
  [UserType.BOT_USER, 'bot_user'],
  [UserType.SYSTEM, 'system'],
  [UserType.UNKNOWN_TYPE, 'unknown'],
]);

export const executionStatuses = new Map([
  [ExecutionStatus.MATCHED, 'Matched'],
  [ExecutionStatus.BOOKED, 'Booked'],
  [ExecutionStatus.CANCELLED, 'Cancelled'],
]);

export const loanStatuses = new Map([
  [ContractStatus.NEW, 'New'],
  [ContractStatus.PENDING, 'Pending'],
  [ContractStatus.REJECTED, 'Rejected'],
  [ContractStatus.DROPPED, 'Dropped'],
  [ContractStatus.CANCELED, 'Canceled'],
  [ContractStatus.OPEN, 'Open'],
  [ContractStatus.CLOSED, 'Closed'],
  [ContractStatus.TERMINATED, 'Terminated'],
  [ContractStatus.UNDER_CORP_ACTION, 'Under Corp Action'],
  [ContractStatus.ROLL_PENDING, 'Roll Pending'],
  [ContractStatus.CANCEL_ROLL_PENDING, 'Cancel Roll Pending'],
  [ContractStatus.CANCEL_RETURN_PENDING, 'Cancel Return Pending'],
  [ContractStatus.CANCEL_NEW_LOAN_PENDING, 'Cancel New Loan Pending'],
  [ContractStatus.ERROR, 'Error'],
]);

export const availableExecutionsAndLoanStatuses = [
  ...[...executionStatuses.entries()].map(([id, name]) => ({
    id,
    name,
    type: 'execution' as const,
  })),
  ...[...loanStatuses.entries()].map(([id, name]) => ({ id, name, type: 'loan' as const })),
];

export const securityTypes = new Map([
  [SecurityType.CS, 'CS'],
  [SecurityType.PFD, 'PFD'],
  [SecurityType.WARRANT, 'WARRANT'],
  [SecurityType.RIGHT, 'RIGHT'],
  [SecurityType.BOND, 'BOND'],
  [SecurityType.ETF, 'ETF'],
  [SecurityType.SP, 'SP'],
  [SecurityType.ADR, 'ADR'],
  [SecurityType.FUND, 'FUND'],
  [SecurityType.BASKET, 'BASKET'],
  [SecurityType.UNIT, 'UNIT'],
  [SecurityType.LT, 'LT'],
  [SecurityType.OS, 'OS'],
  [SecurityType.GDR, 'GDR'],
  [SecurityType.OTHER, 'OTHER'],
  [SecurityType.NYRS, 'NYRS'],
  [SecurityType.AGEN, 'AGEN'],
  [SecurityType.EQLK, 'EQLK'],
  [SecurityType.ETS, 'ETS'],
  [SecurityType.NT, 'NT'],
]);

export const securityCategories = new Map([
  [SecurityCategory.EQUITY, 'Equity'],
  [SecurityCategory.FIXED_INCOME, 'Fixed Income'],
]);
